package com.arena_web.ui.components.chat

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.ChatDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.Avatar
import com.arena_web.ui.components.widgets.AvatarSize
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent

@Composable
fun ChatHeader(
    chat: ChatDto,
    typingUsers: Map<String, String>,
    onlineCount: Int,
    currentUserId: String? = null,
    showInfoPanel: Boolean = false,
    onInfoClick: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val isGroup = chat.type.uppercase() == "GROUP"
    // For direct chats, resolve the conversation partner by excluding the
    // current viewer — otherwise picking the "first non-system member" is a
    // coin-flip that sometimes lands on the viewer themselves.
    val otherMember = if (!isGroup) {
        chat.members.firstOrNull { it.role != "SYSTEM" && it.userId != currentUserId }
            ?: chat.members.firstOrNull { it.role != "SYSTEM" }
    } else null

    Row(
        modifier = modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onInfoClick() }
            .styleModifier {
                property("padding", "${s(22)} ${s(20)} ${s(16)} ${s(18)}")
                property("background", "rgba(60, 121, 241, 0.135)")

                property("border-radius", if (showInfoPanel) "0" else "0 ${s(16)} 0 0")
                property("transition", "border-radius 0.3s ease")
                property("flex-shrink", "0")
                property("gap", s(10))
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Direct chats with a generated player card: render the same
        // Highlight-sized PlayerCard used in the followers list. Groups
        // (and direct chats without a card) keep the round Avatar.
        val cardFrameUrl = otherMember?.cardImageUrl
        if (!isGroup && cardFrameUrl != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("flex-shrink", "0")
                    property("width", s(40))
                    property("height", s(60))
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = otherMember.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            }
        } else {
            Avatar(
                imageUrl = chat.imageUrl,
                name = chat.name ?: t.chatDefaultName,
                size = AvatarSize.SMALL
            )
        }

        Column(
            modifier = Modifier.styleModifier {
                property("gap", s(2))
            }
        ) {
            SpanText(
                chat.name ?: t.chatDefaultName,
                modifier = Modifier.bodyXlMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )

            if (typingUsers.isNotEmpty()) {
                val typingText = if (typingUsers.size == 1) {
                    t.chatTypingOne(typingUsers.values.first())
                } else {
                    t.chatTypingMany(typingUsers.size)
                }
                SpanText(
                    typingText,
                    modifier = Modifier.bodyMRegular().color(sportColors.primary)
                )
            } else if (isGroup) {
                Row(
                    modifier = Modifier.styleModifier { property("gap", s(4)) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Green dot
                    if (onlineCount > 0) {
                        Box(
                            modifier = Modifier
                                .sSize(6)
                                .borderRadius(50.percent)
                                .backgroundColor(AppColors.PrimaryMint)
                                .styleModifier {
                                    property("border", "0.5px solid var(--l-s3, #02040b)")
                                    property("flex-shrink", "0")
                                }
                        )
                    }
                    SpanText(
                        if (onlineCount > 0) t.chatMembersCountOnline(chat.members.size, onlineCount)
                        else t.chatMembersCount(chat.members.size),
                        modifier = Modifier.bodyMRegular().color(if (onlineCount > 0) org.jetbrains.compose.web.css.Color(AppColors.PrimaryMint.toString()) else com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            } else if (onlineCount > 0) {
                Row(
                    modifier = Modifier.styleModifier { property("gap", s(4)) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Green dot
                    Box(
                        modifier = Modifier
                            .sSize(6)
                            .borderRadius(50.percent)
                            .backgroundColor(AppColors.PrimaryMint)
                            .styleModifier {
                                property("border", "0.5px solid var(--l-s3, #02040b)")
                                property("flex-shrink", "0")
                            }
                    )
                    SpanText(
                        t.chatOnline,
                        modifier = Modifier.bodyMRegular().color(AppColors.PrimaryMint)
                    )
                }
            }
        }
    }
}
